import type { ComponentPropsWithRef } from "react";
import { useEffect } from "react";
import { styled } from "styled-components";

import { zIndex } from "common/constants/zIndex";
import { getHexWithAlpha } from "common/utilities";

type StaticOverlayProps = ComponentPropsWithRef<"div">;
type ToggleableOverlayProps = ComponentPropsWithRef<"div"> & { isActive: boolean };

/**
 * This overlay is made active/inactive by adding it to/removing it from the DOM
 */
export const StaticOverlay = (props: StaticOverlayProps) => {
  useEffect(() => {
    document.body.style.overflow = "clip";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return <Container {...props} $isActive />;
};

/**
 * This overlay is made active/inactive by setting its `isActive` property accordingly
 */
export const ToggleableOverlay = ({ isActive, ...rest }: ToggleableOverlayProps) => {
  useEffect(() => {
    document.body.style.overflow = isActive ? "clip" : "unset";
  }, [isActive]);

  return <Container {...rest} $isActive={isActive} />;
};

const Container = styled.div<{ $isActive: boolean }>`
  background-color: ${({ theme }) => getHexWithAlpha(theme.colors.fillPrimary, 0.4)};
  inset: 0;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  position: fixed;
  transition:
    opacity 0.5s,
    visibility 0.5s;
  visibility: ${({ $isActive }) => ($isActive ? "visible" : "hidden")};
  z-index: ${zIndex.overlay};
`;
